import React, { FC, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';
import { Box, Button, Checkbox, TextField } from '@mui/material';
import { DataGrid, GridPaginationModel, GridSortDirection, GridSortModel, useGridApiRef } from '@mui/x-data-grid';
import type { GridColDef } from '@mui/x-data-grid/models/colDef/gridColDef';
import { CustomSelect } from 'components/elements';
import toastService from 'components/elements/Toastify';
import { IAliasFormValues, ManageAliasModal } from 'components/modules/ManageAliasModal';
import { UtilityMapProcessingWarning } from 'components/modules/UtilityMapProcessingWarning';
import { ROUTES } from 'routes';
import {
	clearAliasesByProviderData,
	createAliasesByProvider,
	getAliasesByProvider,
	getAliasesByProviderIsLoadingSelector,
	getAliasesByProviderSelector,
	updateAliasesByProvider,
} from 'store/aliases-by-provider';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { clearStreetTypesData, getStreetsTypes } from 'store/streets-types';
import { IErrorResponse, IQuery, IStreetsDetails, IUtilityProviderDetails, IUtilityProvidersResponse } from 'types';
import { PAGINATION_LIMIT } from 'utils/constants';
import { DATE_FORMAT, SORT_DIR, STREETS_FIELDS_NAME } from 'utils/enums';
import { Rights } from 'utils/enums/rights.enum';
import { formatQueryString, getErrorMessage, parseQueryString } from 'utils/helpers';
import { formatDateView } from 'utils/helpers/date';
import { getTableSetting } from 'utils/helpers/table-setting';
import { useCityAutocomplete, useDebounce, useModal, useUtilityMapStatus } from 'utils/hooks';
import { usePermissions } from 'utils/hooks/use-permissions';
import { useColumns } from 'utils/hooks/use-selection-fields';
import { ProvidersService } from 'utils/services';
import { COLUMNS_CONFIG } from './config';
import s from './index.module.scss';

const DEVICE_FP = '6037e65eefbdasdfadsfasdf3';
const OPTIONS_LIMIT = 100;
const STREET_ID_DEBOUNCE = 500;

const normalizeUtilityProviders = (response: IUtilityProvidersResponse): IUtilityProviderDetails[] =>
	Array.isArray(response) ? response : response.results || [];

const loadAllUtilityProviders = async (): Promise<IUtilityProviderDetails[]> => {
	const results: IUtilityProviderDetails[] = [];
	let offset = 0;

	while (true) {
		const response = await ProvidersService.getUtilityProviders({
			offset,
			limit: OPTIONS_LIMIT,
			deviceFP: DEVICE_FP,
		});
		const batch = normalizeUtilityProviders(response.data);

		results.push(...batch);

		if (batch.length < OPTIONS_LIMIT) {
			break;
		}

		offset += OPTIONS_LIMIT;
	}

	return results;
};

export const AliasesByProviderPage: FC = () => {
	const { search } = useLocation();
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const checkRights = usePermissions();
	const aliasesByProviderList = useAppSelector(getAliasesByProviderSelector);
	const isLoading = useAppSelector(getAliasesByProviderIsLoadingSelector);
	const apiRef = useGridApiRef();
	const [columnWidths, setColumnWidths] = useState({});
	const [isCreateModalOpen, handleOpenCreateModal, handleCloseCreateModal] = useModal();
	const [providerFilter, setProviderFilter] = useState<string>('');
	const [providerOptions, setProviderOptions] = useState<{ title: string; value: string }[]>([]);
	const [streetIdInput, setStreetIdInput] = useState<string>('');
	const [isUndefinedChecked, setIsUndefinedChecked] = useState<boolean>(false);
	const debouncedStreetIdInput = useDebounce(streetIdInput.trim(), STREET_ID_DEBOUNCE);
	const { status: utilityMapStatus } = useUtilityMapStatus('byStreet');
	const {
		cityOptions,
		handleLoadMoreCities,
		handleSearchCities,
		hasMoreCityOptions,
		isCityOptionsLoading,
		resetCityAutocomplete,
	} = useCityAutocomplete({ deviceFP: DEVICE_FP });

	const query = useMemo(() => parseQueryString<IQuery>(search, { parseBooleans: true, parseNumbers: true }), [search]);
	const streetId = useMemo<string | null | undefined>(() => {
		if (isUndefinedChecked) {
			return null;
		}

		return streetIdInput.trim() ? debouncedStreetIdInput || undefined : undefined;
	}, [debouncedStreetIdInput, isUndefinedChecked, streetIdInput]);

	const requestBody = useMemo(
		() => ({
			offset: (query.page || 0) * (query.pageSize || 0),
			limit: query.pageSize || PAGINATION_LIMIT.SMALL,
			orderBy: [
				{
					field: query.field || STREETS_FIELDS_NAME.NAME,
					direction: query.sort || SORT_DIR.ASC,
				},
			],
			searchBy: {
				name: query.searchQuery,
				streetId,
				providerName: providerFilter || undefined,
			},
			deviceFP: DEVICE_FP,
		}),
		[query, providerFilter, streetId]
	);

	useEffect(() => {
		dispatch(getAliasesByProvider(requestBody));
	}, [dispatch, requestBody]);

	useEffect(() => {
		const loadProviderOptions = async () => {
			try {
				const providers = await loadAllUtilityProviders();

				setProviderOptions(
					providers.map(({ name }) => ({
						title: name,
						value: name,
					}))
				);
			} catch (error) {
				toastService.error(getErrorMessage(error as AxiosError<IErrorResponse>));
			}
		};

		void loadProviderOptions();
	}, []);

	useEffect(() => {
		dispatch(
			getStreetsTypes({
				offset: 0,
				limit: PAGINATION_LIMIT.BIG,
				deviceFP: DEVICE_FP,
			})
		);

		return () => {
			dispatch(clearAliasesByProviderData());
			dispatch(clearStreetTypesData());
		};
	}, [dispatch]);

	useEffect(() => {
		setColumnWidths(getTableSetting(ROUTES.aliasesByProvider));
	}, [requestBody]);

	COLUMNS_CONFIG.forEach((item: GridColDef) => {
		// @ts-ignore
		if (columnWidths && columnWidths[item.field]) {
			// @ts-ignore
			item.width = columnWidths[item.field];
			item.flex = undefined;
		}
	});

	const handleColumnResize = (params: any) => {
		const newWidths = {
			...columnWidths,
			[params.colDef.field]: params.width,
		};
		setColumnWidths(newWidths);
		localStorage.setItem(ROUTES.aliasesByProvider, JSON.stringify(newWidths));
	};

	const handleChangePage = ({ page, pageSize }: GridPaginationModel): void => {
		navigate(
			{
				search: formatQueryString({
					...query,
					page,
					pageSize,
				}),
			},
			{ replace: true }
		);
	};

	const handleCellEdit = async (newRow: IStreetsDetails, row: IStreetsDetails): Promise<IStreetsDetails> => {
		const { id, streetId: nextStreetId } = newRow;

		try {
			const { payload } = await dispatch(
				updateAliasesByProvider({
					id,
					streetId: nextStreetId,
					deviceFP: DEVICE_FP,
				})
			);

			if (payload.errorData) {
				console.warn('Payload error data:', payload.errorData);
				return row;
			}

			const { street } = payload;
			return {
				...newRow,
				street: street.name,
				streetType: street.streetType.name,
				city: street.city.name,
			};
		} catch (error: any) {
			console.error('Error updating aliases:', error.message || error);
			return row;
		}
	};

	const handleSortField = (model: GridSortModel): void => {
		const { field, sort } = model[0] || {};
		navigate(
			{
				search: formatQueryString({
					...query,
					field,
					sort,
					page: 0,
				}),
			},
			{ replace: true }
		);
	};

	const handleChangeStreetId = (event: React.ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setStreetIdInput(value);
		setIsUndefinedChecked(false);
	};

	const handleCheckStreetId = (event: React.ChangeEvent<HTMLInputElement>) => {
		const { checked } = event.target;
		setIsUndefinedChecked(checked);

		if (checked) {
			setStreetIdInput('');
		}
	};

	const handleClearFilters = () => {
		setIsUndefinedChecked(false);
		setStreetIdInput('');
		setProviderFilter('');

		if (query.searchQuery || query.page) {
			navigate(
				{
					search: formatQueryString({
						...query,
						searchQuery: undefined,
						page: 0,
					}),
				},
				{ replace: true }
			);
		}
	};

	const handleCreateAlias = async (values: IAliasFormValues) => {
		try {
			await dispatch(
				createAliasesByProvider({
					name: values.name.trim(),
					...(values.streetTypeId ? { streetTypeId: values.streetTypeId } : {}),
					...(values.streetId.trim() ? { streetId: values.streetId.trim() } : {}),
					...(values.cityId ? { cityId: Number(values.cityId) } : {}),
					...(values.providerId ? { providerId: Number(values.providerId) } : {}),
					deviceFP: DEVICE_FP,
				})
			).unwrap();

			dispatch(getAliasesByProvider(requestBody));
			handleCloseCreateModal();
			toastService.success('Аліас успішно додано');
		} catch (error: any) {
			toastService.error(getErrorMessage(error));
		}
	};

	const rows = useMemo(
		() =>
			aliasesByProviderList?.results.map((alias: any) => ({
				...alias,
				cityCE: alias.city.name,
				cityShorPrefixCE: alias.city.shorPrefix,
				cityPrefixCE: alias.city.prefix,
				'streetType.name': alias.streetType.name,
				city: alias.street !== null && alias.street.city.name,
				streetType: alias.street !== null && alias.street.streetType.name,
				street: alias.street !== null && alias.street.name,
				'provider.name': alias.provider.name,
				createdAt: formatDateView(alias.createdAt, DATE_FORMAT.DATE_FULL),
				updatedAt: formatDateView(alias.updatedAt, DATE_FORMAT.DATE_FULL),
			})),
		[aliasesByProviderList]
	);

	const providerFilterOptions = useMemo(
		() => [{ value: '', title: 'Показати будь-який' }, ...providerOptions],
		[providerOptions]
	);

	return (
		<>
			<UtilityMapProcessingWarning status={utilityMapStatus} />
			<Box className={s.toolbar}>
				<Box className={s.filter}>
					<Box>
						<CustomSelect
							value={providerFilter}
							name="Провайдер"
							onChange={(event: any) => setProviderFilter(event.target.value)}
							placeholder="Провайдер"
							options={providerFilterOptions}
						/>
					</Box>
					<TextField value={streetIdInput} placeholder="Пошук по айді вулиці" onChange={handleChangeStreetId} />
					<Box className={s.filter__checkbox}>
						<Checkbox checked={isUndefinedChecked} onChange={handleCheckStreetId} />
						<Box>Показати лише невизначені</Box>
					</Box>
					<Button variant="contained" onClick={handleClearFilters}>
						Очистити
					</Button>
				</Box>
				{checkRights(Rights.STREETS_MANAGE) && (
					<Button
						variant="contained"
						onClick={() => {
							resetCityAutocomplete();
							handleOpenCreateModal();
						}}
					>
						Додати
					</Button>
				)}
			</Box>

			<Box className={s.tableWrapper}>
				<DataGrid
					disableColumnFilter
					onColumnResize={handleColumnResize}
					apiRef={apiRef}
					disableRowSelectionOnClick
					rows={rows || []}
					columns={useColumns(COLUMNS_CONFIG)}
					rowCount={aliasesByProviderList?.total || 0}
					loading={isLoading}
					getCellClassName={(params) => (params.colDef.type === 'custom' ? s.custom : '')}
					initialState={{
						pagination: {
							paginationModel: { page: query.page || 0, pageSize: query.pageSize || PAGINATION_LIMIT.SMALL },
						},
						sorting: {
							sortModel: [{ field: query.field || STREETS_FIELDS_NAME.CREATE_AT, sort: query.sort as GridSortDirection }],
						},
					}}
					slotProps={{
						pagination: {
							labelRowsPerPage: 'Показувати :',
							page: query.page || 0,
							rowsPerPage: query.pageSize || PAGINATION_LIMIT.SMALL,
						},
					}}
					processRowUpdate={handleCellEdit}
					paginationMode="server"
					sortingMode="server"
					onSortModelChange={handleSortField}
					onPaginationModelChange={handleChangePage}
					className={s.table}
					pageSizeOptions={[PAGINATION_LIMIT.SMALL, PAGINATION_LIMIT.MIDDLE, PAGINATION_LIMIT.BIG]}
				/>
			</Box>

			<ManageAliasModal
				title="Створити аліас вулиці"
				isOpen={isCreateModalOpen}
				onClose={handleCloseCreateModal}
				initialValues={{
					name: '',
					streetTypeId: '',
					streetId: '',
					cityId: '',
					providerId: '',
				}}
				onSubmit={handleCreateAlias}
				cityOptions={cityOptions}
				cityOptionsLoading={isCityOptionsLoading}
				onCitySearch={handleSearchCities}
				onCityLoadMore={handleLoadMoreCities}
				hasMoreCityOptions={hasMoreCityOptions}
				buttonText="Додати"
			/>
		</>
	);
};
